/**
 * CareManagerView — the care-manager view (Soak #2 top-bar #11): given a
 * roster id, show the care manager and the LIST OF THEIR CLIENTS. Reached
 * from the record header's "Care manager" link (VitalsBand); the destination
 * the founder's feedback asked for but that did not exist yet.
 *
 * Route: /t/:tenantSlug/care-managers/:rosterId (DETAIL_ROUTES → 'clients').
 *
 * Gate — mirrors the Clients roster (founder 2026-07-06, SOAK SCOPING):
 * page + fetch gate on canViewAllClinical; the roster endpoint re-derives
 * the same gate server-side, so a denied role fires NO request (the
 * PageShell access-as-prop contract keeps hooks above the gate, React #310
 * discipline).
 *
 * WARNING — the per-CM scoping here (clientHasCareManager: she leads the
 * client or is an additional care manager on it) is a CLIENT-SIDE filter
 * over a FULL-tenant-roster fetch (GET /clients). It is
 * NOT an access boundary. This is safe TODAY only because view_all_clinical
 * already grants all-clients visibility — every viewer here is already
 * entitled to the whole roster. Do NOT widen this gate to unit-peers / line
 * CMs (or any caseload-scoped role) by editing canViewAllClinical alone:
 * that would ship the FULL tenant roster to the browser and merely hide the
 * other CMs' clients with a client-side filter — a PHI leak. Any such
 * widening MUST be paired with a SERVER-SIDE per-CM roster fetch (a
 * caseload-scoped /clients variant that returns only that CM's clients),
 * so the wire never carries clients the caller may not see.
 *
 * Reuse, not duplication (founder instruction): the client list is the SAME
 * useClientsList roster query, filtered client-side to this CM's clients
 * (the ones she leads and the ones she shares as an additional care
 * manager), and rendered with the SAME ClientRow the roster uses (hideCm —
 * every row here is hers, so the per-row "CM:" line would be noise). Her
 * name resolves from the matched rows first (primary_cm_name where she
 * leads, her additional-care-manager entry where she shares) and her unit
 * from a row she leads, each falling back to the assign-options roster for
 * a CM with zero active clients.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canOpenEveryClientRecord } from '@/lib/capabilities';
import { clientHasCareManager, useClientsList } from '@/lib/clientsApi';
import { lifecycleListRank } from '@/lib/clientRecord';
import { Button } from '@/components/primitives/Button';
import { useAssignOptions } from '@/lib/workspace';
import { PageShell } from '@/components/primitives/PageShell';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { BackToClients } from '@/components/BackToClients';
import { ClientRow } from '@/pages/Clients';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';

export function CareManagerView(): ReactNode {
  const { tenantSlug = '', rosterId = '' } = useParams<{ tenantSlug: string; rosterId: string }>();
  const { whoami } = useTenant();
  const contact = useSupportContact();
  // SOAK SCOPING (founder, 2026-07-06): the same surface gate as the roster.
  const allowed = canOpenEveryClientRecord(whoami);

  const { clients, isLoading, isError, error, refetch } = useClientsList(tenantSlug, {
    enabled: allowed,
  });
  const { careManagers, units } = useAssignOptions(tenantSlug, { enabled: allowed });

  // Her clients, each by name: the active ones on the page, and every
  // client who is not 'active' (inactive awaiting payment, deceased with
  // family work continuing) behind "Show inactive" at the foot, hidden
  // until asked for (owner's ask 2026-11-02: a care manager should not
  // open her page onto a client the office is waiting on for payment).
  const myClients = useMemo(
    () =>
      clients
        .filter((c) => clientHasCareManager(c, rosterId))
        .sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [clients, rosterId],
  );
  const activeClients = useMemo(
    () => myClients.filter((c) => lifecycleListRank(c.lifecycle_status ?? 'active') === 0),
    [myClients],
  );
  const inactiveClients = useMemo(
    () => myClients.filter((c) => lifecycleListRank(c.lifecycle_status ?? 'active') === 1),
    [myClients],
  );
  const [showInactive, setShowInactive] = useState(false);

  // Resolve the CM identity from the matched rows first: her name where she
  // leads (primary_cm_name) or where she is an additional care manager. A
  // row's unit_name is its LEAD's unit, so only a row she leads names hers.
  // Both fall back to the assign-options roster, so a CM with no active
  // clients still names right.
  const cmFromOptions = careManagers.find((cm) => cm.roster_id === rosterId);
  const ledClients = myClients.filter((c) => c.primary_cm_roster_id === rosterId);
  const cmKnownName =
    ledClients[0]?.primary_cm_name ??
    myClients.flatMap((c) => c.additional_care_managers).find((p) => p.roster_id === rosterId)?.name ??
    cmFromOptions?.name ??
    null;
  const cmName = cmKnownName ?? 'This care manager';
  // The empty state names the person; with no name known it reads "this care manager".
  const cmNameInLine = cmKnownName ?? 'this care manager';
  const unitName =
    ledClients.find((c) => c.unit_name !== null)?.unit_name ??
    (cmFromOptions?.unit_id
      ? (units.find((u) => u.id === cmFromOptions.unit_id)?.name ?? null)
      : null);

  const count = activeClients.length;

  const deniedContent = (
    <>
      <div
        role="alert"
        className="my-3 p-3 border border-warm-300 bg-warm-50 rounded-card text-sm text-ink"
      >
        {contactSentence(
          "The care manager view isn't part of your access yet. Ask {contact_admin} if your work needs it.",
          contact,
        )}
      </div>
      <Link
        to={`/t/${tenantSlug}/clients`}
        className="inline-block mt-4 min-h-11 text-sm text-sage-700 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      >
        Back to clients
      </Link>
    </>
  );

  return (
    <PageShell
      eyebrow="Care manager"
      title={cmName}
      subtitle={unitName ?? undefined}
      maxWidth="wide"
      testId="care-manager-page"
      access={{ allowed, deniedContent }}
    >
      <BackToClients tenantSlug={tenantSlug} />

      <div className="mb-3 mt-1 flex items-center">
        <span className="text-xs text-ink-soft" data-testid="cm-client-count">
          {count.toLocaleString()} {count === 1 ? 'client' : 'clients'}
        </span>
      </div>

      <AsyncBoundary
        isLoading={isLoading}
        isError={isError}
        error={error}
        onRetry={refetch}
        isEmpty={!isLoading && !isError && myClients.length === 0}
        emptyHeadline={`No clients for ${cmNameInLine} yet.`}
        emptyBody={`When the office assigns ${cmNameInLine} a client, you’ll see it here.`}
      >
        <div
          className="rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] overflow-hidden"
          data-testid="cm-clients-list"
        >
          {activeClients.length === 0 ? (
            <p className="px-4 py-3 text-sm text-ink-soft" data-testid="cm-no-active-clients">
              No active clients.
            </p>
          ) : (
            activeClients.map((c) => <ClientRow key={c.id} client={c} slug={tenantSlug} hideCm />)
          )}
          {inactiveClients.length > 0 && (
            <div className="flex justify-center px-4 py-2.5 border-t border-sage-800/[8%]">
              <Button
                size="sm"
                variant="ghost"
                className="!min-h-0 text-xs"
                data-testid="cm-show-inactive"
                aria-pressed={showInactive}
                aria-controls="cm-inactive-clients"
                onClick={() => setShowInactive((v) => !v)}
              >
                {showInactive
                  ? 'Hide inactive'
                  : `Show inactive (${inactiveClients.length.toLocaleString()})`}
              </Button>
            </div>
          )}
        </div>
        {showInactive && inactiveClients.length > 0 && (
          <section className="mt-4" aria-labelledby="cm-inactive-heading">
            <h2 id="cm-inactive-heading" className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-soft">
              Inactive
            </h2>
            <div
              id="cm-inactive-clients"
              className="rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] overflow-hidden"
              data-testid="cm-inactive-list"
            >
              {inactiveClients.map((c) => (
                <ClientRow key={c.id} client={c} slug={tenantSlug} hideCm />
              ))}
            </div>
          </section>
        )}
      </AsyncBoundary>
    </PageShell>
  );
}
